import { ElementNode } from 'lexical';

export class SectionAnchorNode extends ElementNode {
  __sectionId;

  static getType() {
    return 'section-anchor';
  }

  static clone(node) {
    return new SectionAnchorNode(node.__sectionId, node.__key);
  }

  constructor(sectionId, key) {
    super(key);
    this.__sectionId = sectionId;
  }

  static importJSON(serializedNode) {
    const node = $createSectionAnchorNode(serializedNode.sectionId);
    node.setFormat(serializedNode.format);
    node.setIndent(serializedNode.indent);
    node.setDirection(serializedNode.direction);
    return node;
  }

  exportJSON() {
    return {
      ...super.exportJSON(),
      type: 'section-anchor',
      version: 1,
      sectionId: this.__sectionId,
    };
  }

  static importDOM() {
    return {
      span: (domNode) => {
        // Primary: explicit marker (new saves from this editor)
        if (domNode.getAttribute('data-section-anchor') === 'true') {
          return {
            conversion: (element) => ({
              node: $createSectionAnchorNode(element.id || ''),
            }),
            priority: 2,
          };
        }
        // Legacy: spans that have only an id and no class/style/other data-*
        // attributes — these are section anchors created before the data attribute
        // was added to exportDOM, or authored directly in the admin HTML editor.
        const id = domNode.getAttribute('id');
        if (
          id &&
          /^[a-zA-Z][a-zA-Z0-9_-]*$/.test(id) &&
          !domNode.getAttribute('class') &&
          !domNode.getAttribute('style')
        ) {
          return {
            conversion: (element) => ({
              node: $createSectionAnchorNode(element.id),
            }),
            priority: 1,
          };
        }
        return null;
      },
    };
  }

  exportDOM() {
    const el = document.createElement('span');
    el.id = this.__sectionId;
    // Keep the data attribute so the HTML round-trips correctly back into the editor.
    el.setAttribute('data-section-anchor', 'true');
    return { element: el };
  }

  createDOM(config) {
    const el = document.createElement('span');
    el.id = this.__sectionId;
    el.setAttribute('data-section-anchor', 'true');
    // Visual indicator inside the editor only
    el.style.borderBottom = '2px dotted #1565c0';
    el.style.cursor = 'default';
    el.title = `Section anchor: #${this.__sectionId}`;
    return el;
  }

  updateDOM(prevNode, dom) {
    if (prevNode.__sectionId !== this.__sectionId) {
      dom.id = this.__sectionId;
      dom.title = `Section anchor: #${this.__sectionId}`;
    }
    return false;
  }

  isInline() {
    return true;
  }

  canBeEmpty() {
    return false;
  }

  getSectionId() {
    return this.__sectionId;
  }

  setSectionId(sectionId) {
    const writable = this.getWritable();
    writable.__sectionId = sectionId;
  }
}

export function $createSectionAnchorNode(sectionId) {
  return new SectionAnchorNode(sectionId);
}

export function $isSectionAnchorNode(node) {
  return node instanceof SectionAnchorNode;
}
